import Cookies from 'js-cookie'

import { clearAuthToken } from '@/helpers/authToken'
import router from '@/router'

import store from '.'

const parseJson = body => {
  try {
    return JSON.parse(body)
  } catch {
    return undefined
  }
}

const NEVER = () => new Promise(() => {})

const requestHeaders = options => {
  const merchantId = options.merchantId ?? store.state.activeFranchiseMerchantId
  return {
    Authorization: `Bearer ${options.authToken || Cookies.get('auth_token')}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-url': window.location.pathname,
    ...(merchantId && { 'X-Merchant-Id': merchantId })
  }
}

let api = {
  call(method, endpoint, data = {}, options = {}) {
    options = {
      receive: true,
      save: true,
      delete: true,
      abortController: new AbortController(),
      noProcessing: false,
      redirectOnForbidden: true,
      ...options
    }
    if (['POST', 'PUT', 'DELETE'].includes(method) && options.save && !options.noProcessing) store.commit('INC_SAVING')

    return fetch(
      (options.noProcessing
        ? process.env.VUE_APP_API_SERVER.replace('/dashboard/', '/')
        : process.env.VUE_APP_API_SERVER) + endpoint,
      {
        method,
        headers: requestHeaders(options),
        signal: options.abortController.signal,
        ...(method !== 'GET' && { body: JSON.stringify(data) })
      }
    )
      .then(async response => {
        // A no-body response - Rails `head :unauthorized`, or any 204 - makes
        // response.json() throw a SyntaxError that masks the real status, so
        // the caller sees an unparseable error instead of a 401 it can act on.
        const body = await response.text()
        const parsed = body ? parseJson(body) : {}
        const data = parsed ?? {}

        if (response.status === 401) {
          if (data.reason === 'no-stripe-subscription') {
            router.push('/stripe-integration-required')
          }
          if (data.reason === 'canceled-stripe-subscription') {
            router.push('/restart-stripe-integration')
          }
          if (data.reason === 'past-due-stripe-subscription') {
            router.push('/pay-past-due-stripe-balance')
          }
          if (data.reason === 'token-expired') {
            clearAuthToken()
            store.commit('RESET_STATE')
            window.location = '/login'
          }
        }
        if (response.status === 403 && options.redirectOnForbidden) {
          // prevent redirect loops
          if (window.location.pathname !== '/login') {
            window.location = '/login?to=' + window.location.href
          }
        }
        if (!response.ok || parsed === undefined) {
          const error = new Error(
            `${response.status} ${method} /${endpoint}${parsed === undefined ? ' (not a JSON response)' : ''}`
          )
          error.response = {
            status: response.status,
            data
          }
          throw error
        }
        return { ...response, status: response.status, data }
      }) // compat with Axios
      .then(response => {
        if (options.noProcessing) return response

        if (options.receive) {
          if (method === 'DELETE' && options.delete) {
            store.commit('DESTROY_OBJECT', response.data)
          } else {
            store.commit('RECEIVE_OBJECT', response.data)
          }
        }
        return response
      })
      .finally(() => {
        if (['POST', 'PUT', 'DELETE'].includes(method) && options.save && !options.noProcessing)
          store.commit('DEC_SAVING')
      })
      .catch(error => {
        if (error?.name === 'AbortError' && options.quietAbort) return NEVER()
        throw error
      })
  }
}

export default api
